import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Trash2, Lock, ArrowLeft, Users, Settings, Plus } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  getAllTournaments,
  createTournament,
  saveTournament,
  getSignups,
  removeSignup,
  verifyAdminPassword,
  getAdminToken,
  TournamentConfig,
  Signup,
} from '@/services/tournamentService';

const DEFAULT_NEW_TOURNAMENT: Omit<TournamentConfig, 'id'> = {
  date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  time: '18:30',
  duration: 90,
  location: 'Padelon Trondheim, Fossegrenda',
  locationUrl: 'https://maps.app.goo.gl/7ZxUZPjZHUzavzi2A',
  courts: [1, 2, 3],
  maxPlayers: 12,
  level: 'Alle nivåer / All levels',
  announcementText: 'Sosial turnering med NTNUi Padel! Vi gleder oss!',
  announcementTextEn: 'Social tournament with NTNUi Padel! We are excited!',
  tournamentFormat: 'americano',
  doorCode: '2022#',
};

export default function Admin() {
  const { language } = useLanguage();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [tournaments, setTournaments] = useState<TournamentConfig[]>([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(null);
  const [config, setConfig] = useState<TournamentConfig | null>(null);
  const [signups, setSignups] = useState<Signup[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Check for existing token on mount (persistent login)
  useEffect(() => {
    const token = getAdminToken();
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  // Load all tournaments on auth
  useEffect(() => {
    if (isAuthenticated) {
      getAllTournaments().then(ts => {
        // Sort by date descending (newest first)
        const sorted = ts.sort((a, b) => b.date.localeCompare(a.date));
        setTournaments(sorted);
        if (sorted.length > 0) {
          setSelectedTournamentId(sorted[0].id);
        }
      });
    }
  }, [isAuthenticated]);

  // Load selected tournament config
  useEffect(() => {
    if (selectedTournamentId && tournaments.length > 0) {
      const selected = tournaments.find(t => t.id === selectedTournamentId);
      if (selected) {
        setConfig(selected);
        if (selected.date) {
          const [y, m, d] = selected.date.split('-').map(Number);
          setSelectedDate(new Date(y, m - 1, d));
        } else {
          setSelectedDate(undefined);
        }
      }
    }
  }, [selectedTournamentId, tournaments]);

  // Load signups when config changes
  useEffect(() => {
    if (isAuthenticated && config) {
      getSignups(config.id).then(setSignups);
    }
  }, [isAuthenticated, config?.id]);

  const handleLogin = async () => {
    setLoading(true);
    const result = await verifyAdminPassword(password);
    setLoading(false);
    if (result.success) {
      setIsAuthenticated(true);
    } else {
      const fallbackMsg = language === 'no' ? 'Innlogging feilet' : 'Login failed';
      toast.error(result.error || fallbackMsg);
    }
  };

  const handleCreateNew = async () => {
    setLoading(true);
    try {
      const created = await createTournament(DEFAULT_NEW_TOURNAMENT);
      setTournaments(prev => [created, ...prev]);
      setSelectedTournamentId(created.id);
      toast.success(language === 'no' ? 'Ny turnering opprettet!' : 'New tournament created!');
    } catch (e: any) {
      toast.error(e.message || 'Failed to create tournament');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!config) return;
    try {
      const saved = await saveTournament(config);
      // Update in list
      setTournaments(prev => prev.map(t => t.id === saved.id ? saved : t));
      setConfig(saved);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 1500);
      toast.success(language === 'no' ? 'Innstillinger lagret!' : 'Settings saved!');
    } catch (e: any) {
      toast.error(e.message || 'Save failed');
    }
  };

  const handleRemoveSignup = async (id: string) => {
    if (!config) return;
    if (window.confirm(language === 'no' ? 'Fjerne denne påmeldingen?' : 'Remove this signup?')) {
      const ok = await removeSignup(config.id, id);
      if (ok) {
        const updated = await getSignups(config.id);
        setSignups(updated);
        toast.success(language === 'no' ? 'Påmelding fjernet' : 'Signup removed');
      } else {
        toast.error('Failed to remove signup');
      }
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      const localDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      setConfig(prev => prev ? { ...prev, date: localDate } : prev);
      setCalendarOpen(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Admin
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{language === 'no' ? 'Passord' : 'Password'}</Label>
              <Input
                autoFocus
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                placeholder="••••••••"
              />
            </div>
            <Button className="w-full" onClick={handleLogin} disabled={loading}>
              {loading ? '...' : (language === 'no' ? 'Logg inn' : 'Log in')}
            </Button>
            <Button variant="ghost" className="w-full" onClick={() => window.location.href = '/'}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {language === 'no' ? 'Tilbake' : 'Back'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!config) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold flex items-center gap-2 text-foreground">
            <Settings className="h-6 w-6" />
            {language === 'no' ? 'Turneringsadmin' : 'Tournament Admin'}
          </h1>
          <Button variant="outline" size="sm" onClick={() => window.location.href = '/'}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {language === 'no' ? 'Tilbake' : 'Back'}
          </Button>
        </div>

        {/* Tournament Selector */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{language === 'no' ? 'Velg turnering' : 'Select Tournament'}</span>
              <Button size="sm" onClick={handleCreateNew} disabled={loading}>
                <Plus className="mr-2 h-4 w-4" />
                {language === 'no' ? 'Ny turnering' : 'New Tournament'}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedTournamentId || ''} onValueChange={setSelectedTournamentId}>
              <SelectTrigger>
                <SelectValue placeholder={language === 'no' ? 'Velg turnering' : 'Select tournament'} />
              </SelectTrigger>
              <SelectContent className="bg-popover border shadow-md z-50">
                {tournaments.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.date} — {t.location} ({t.tournamentFormat})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground mt-2">
              {tournaments.length} {language === 'no' ? 'turneringer totalt' : 'tournaments total'}
              {signups.length > 0 && ` • ${signups.length} ${language === 'no' ? 'påmeldte' : 'signups'}`}
            </p>
          </CardContent>
        </Card>

        {/* Tournament Settings */}
        <Card>
          <CardHeader>
            <CardTitle>{language === 'no' ? 'Turneringsinnstillinger' : 'Tournament Settings'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Date */}
            <div className="space-y-1">
              <Label className="font-semibold">{language === 'no' ? 'Dato' : 'Date'}</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left", !selectedDate && "text-muted-foreground font-normal", selectedDate && "font-semibold")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, 'EEEE dd.MM.yyyy') : (language === 'no' ? 'Velg dato' : 'Pick a date')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-background border shadow-md z-50" align="start">
                  <Calendar mode="single" selected={selectedDate} onSelect={handleDateSelect} initialFocus className={cn("p-3 pointer-events-auto")} />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time */}
            <div className="space-y-1">
              <Label className="font-semibold">{language === 'no' ? 'Klokkeslett' : 'Time'}</Label>
              <Input type="text" placeholder="HH:MM" value={config.time} onChange={e => {
                const val = e.target.value.replace(/[^\d:]/g, '');
                setConfig(prev => prev ? { ...prev, time: val } : prev);
              }} />
            </div>

            {/* Duration */}
            <div className="space-y-1">
              <Label className="font-semibold">{language === 'no' ? 'Varighet (minutter)' : 'Duration (minutes)'}</Label>
              <Input type="number" value={config.duration} onChange={e => setConfig(prev => prev ? { ...prev, duration: Number(e.target.value) } : prev)} />
            </div>

            {/* Location */}
            <div className="space-y-1">
              <Label className="font-semibold">{language === 'no' ? 'Sted' : 'Location'}</Label>
              <Input value={config.location} onChange={e => setConfig(prev => prev ? { ...prev, location: e.target.value } : prev)} />
            </div>

            {/* Location URL */}
            <div className="space-y-1">
              <Label className="font-semibold">{language === 'no' ? 'Kart-lenke' : 'Map URL'}</Label>
              <Input value={config.locationUrl} onChange={e => setConfig(prev => prev ? { ...prev, locationUrl: e.target.value } : prev)} />
            </div>

            {/* Courts */}
            <div className="space-y-2">
              <Label className="font-semibold">{language === 'no' ? 'Baner' : 'Courts'}</Label>
              <div className="flex flex-wrap gap-4">
                {[1, 2, 3, 4, 5, 6, 7].map(courtNum => {
                  const isSelected = config.courts.includes(courtNum);
                  return (
                    <label key={courtNum} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => {
                          setConfig(prev => {
                            if (!prev) return prev;
                            const newCourts = checked
                              ? [...prev.courts, courtNum].sort((a, b) => a - b)
                              : prev.courts.filter(c => c !== courtNum);
                            if (newCourts.length === 0) return prev;
                            return { ...prev, courts: newCourts, maxPlayers: newCourts.length * 4 };
                          });
                        }}
                      />
                      <span className="text-sm font-medium">{language === 'no' ? 'Bane' : 'Court'} {courtNum}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-sm text-muted-foreground">
                {config.courts.length} {language === 'no' ? 'baner valgt' : 'courts selected'} = {config.courts.length * 4} {language === 'no' ? 'spillere' : 'players'}
              </p>
            </div>

            {/* Level */}
            <div className="space-y-1">
              <Label className="font-semibold">{language === 'no' ? 'Nivå' : 'Level'}</Label>
              <Input value={config.level} onChange={e => setConfig(prev => prev ? { ...prev, level: e.target.value } : prev)} />
            </div>

            {/* Format */}
            <div className="space-y-1">
              <Label className="font-semibold">{language === 'no' ? 'Turneringsformat' : 'Tournament Format'}</Label>
              <Select value={config.tournamentFormat} onValueChange={(v: 'mexicano' | 'americano' | 'team_americano') => setConfig(prev => prev ? { ...prev, tournamentFormat: v } : prev)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border shadow-md z-50">
                  <SelectItem value="americano">Americano</SelectItem>
                  <SelectItem value="mexicano">Mexicano</SelectItem>
                  <SelectItem value="team_americano">Team Americano</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Door Code */}
            <div className="space-y-1">
              <Label className="font-semibold">{language === 'no' ? 'Dørkode' : 'Door Code'}</Label>
              <Input value={config.doorCode} onChange={e => setConfig(prev => prev ? { ...prev, doorCode: e.target.value } : prev)} />
            </div>

            {/* Announcement NO */}
            <div className="space-y-1">
              <Label className="font-semibold">{language === 'no' ? 'Annonseringstekst (norsk)' : 'Announcement (Norwegian)'}</Label>
              <Textarea value={config.announcementText} onChange={e => setConfig(prev => prev ? { ...prev, announcementText: e.target.value } : prev)} rows={3} />
            </div>

            {/* Announcement EN */}
            <div className="space-y-1">
              <Label className="font-semibold">{language === 'no' ? 'Annonseringstekst (engelsk)' : 'Announcement (English)'}</Label>
              <Textarea value={config.announcementTextEn} onChange={e => setConfig(prev => prev ? { ...prev, announcementTextEn: e.target.value } : prev)} rows={3} />
            </div>

            <Button
              className={cn("w-full transition-colors duration-300", saveSuccess && "bg-emerald-600 hover:bg-emerald-600 dark:bg-emerald-500 dark:hover:bg-emerald-500")}
              onClick={handleSave}
            >
              {language === 'no' ? 'Lagre innstillinger' : 'Save Settings'}
            </Button>
          </CardContent>
        </Card>

        {/* Signups Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {language === 'no' ? 'Påmeldinger' : 'Signups'} ({signups.length}/{config.maxPlayers})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {signups.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                {language === 'no' ? 'Ingen påmeldinger ennå' : 'No signups yet'}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>{language === 'no' ? 'Navn' : 'Name'}</TableHead>
                    <TableHead>{language === 'no' ? 'E-post' : 'Email'}</TableHead>
                    <TableHead>{language === 'no' ? 'Telefon' : 'Phone'}</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {signups.map((s, i) => (
                    <TableRow key={s.id} className={i >= config.maxPlayers ? 'opacity-60' : ''}>
                      <TableCell>
                        {i + 1}
                        {i >= config.maxPlayers && <span className="ml-1 text-xs text-muted-foreground">({language === 'no' ? 'venteliste' : 'waitlist'})</span>}
                      </TableCell>
                      <TableCell>{s.name}{s.partner?.name ? ` & ${s.partner.name}` : ''}</TableCell>
                      <TableCell className="text-sm">{s.email}{s.partner?.email ? `, ${s.partner.email}` : ''}</TableCell>
                      <TableCell className="text-sm font-mono">{s.phone}{s.partner?.phone ? `, ${s.partner.phone}` : ''}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => handleRemoveSignup(s.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
